import React from 'react';
import { Modal, Table, Tag, Timeline, Empty, Divider, Row, Col, Card } from 'antd';
import { useTranslation } from 'react-i18next';
import {
    ClockCircleOutlined,
    FileTextOutlined,
    MedicineBoxOutlined,
    UserOutlined
} from '@ant-design/icons';
import { getToothMetadata, TOOTH_CONDITIONS } from './constants/teethData';
import moment from 'moment';
import './ToothDetailModal.css';

/**
 * ToothDetailModal Component
 * Displays detailed information and treatment history for a specific tooth
 */
const ToothDetailModal = ({
    visible,
    onClose,
    toothNumber,
    toothCondition = 'healthy',
    treatmentHistory = [],
    notes = '',
    images = []
}) => {
    const { t } = useTranslation(['tooth', 'treatment', 'common']);

    if (!toothNumber) return null;

    const metadata = getToothMetadata(toothNumber);
    const conditionData = TOOTH_CONDITIONS[toothCondition] || TOOTH_CONDITIONS.healthy;

    // Table columns for treatment history
    const columns = [
        {
            title: t('treatment:date'),
            dataIndex: 'date',
            key: 'date',
            render: (date) => moment(date).format('YYYY-MM-DD'),
            width: 120
        },
        {
            title: t('treatment:procedure'),
            dataIndex: 'procedure',
            key: 'procedure',
            render: (procedure) => t(`tooth:procedures.${procedure}`, procedure)
        },
        {
            title: t('treatment:condition'),
            dataIndex: 'condition',
            key: 'condition',
            render: (condition) => {
                const condData = TOOTH_CONDITIONS[condition] || TOOTH_CONDITIONS.healthy;
                return (
                    <Tag color={condData.borderColor}>
                        {condData.icon} {t(condData.label)}
                    </Tag>
                );
            }
        },
        {
            title: t('treatment:dentist'),
            dataIndex: 'dentist',
            key: 'dentist',
            render: (dentist) => (
                <span>
                    <UserOutlined /> {dentist?.name || t('common:unknown')}
                </span>
            )
        }
    ];

    return (
        <Modal
            title={
                <div className="tooth-detail-modal-title">
                    <div className="tooth-detail-title-content">
                        <span className="tooth-icon" style={{ color: conditionData.borderColor }}>
                            {conditionData.icon}
                        </span>
                        <div>
                            <div className="tooth-name">
                                {t(`tooth:names.${toothNumber}`, metadata.name)}
                            </div>
                            <div className="tooth-subtitle">
                                {t('tooth:number')}: {toothNumber} ({metadata.shortName})
                            </div>
                        </div>
                    </div>
                </div>
            }
            open={visible}
            onCancel={onClose}
            footer={null}
            width={800}
            className="tooth-detail-modal"
        >
            {/* Current Condition Card */}
            <Card
                size="small"
                className="tooth-condition-card"
                style={{
                    backgroundColor: conditionData.color,
                    borderColor: conditionData.borderColor
                }}
            >
                <Row gutter={16}>
                    <Col span={12}>
                        <div className="condition-label">{t('tooth:currentCondition')}:</div>
                        <div className="condition-value">
                            {conditionData.icon} <strong>{t(conditionData.label)}</strong>
                        </div>
                    </Col>
                    <Col span={12}>
                        <div className="condition-label">{t('tooth:type')}:</div>
                        <div className="condition-value">
                            <strong>{t(`tooth:types.${metadata.type}`, metadata.type)}</strong>
                        </div>
                    </Col>
                </Row>
            </Card>

            <Divider orientation="left">
                <ClockCircleOutlined /> {t('treatment:history')}
            </Divider>

            {/* Treatment History Timeline */}
            {treatmentHistory && treatmentHistory.length > 0 ? (
                <>
                    <Timeline mode="left" className="tooth-treatment-timeline">
                        {treatmentHistory.slice(0, 5).map((treatment, index) => {
                            const treatmentCondition = TOOTH_CONDITIONS[treatment.condition] || TOOTH_CONDITIONS.healthy;
                            return (
                                <Timeline.Item
                                    key={index}
                                    color={treatmentCondition.borderColor}
                                    label={moment(treatment.date).format('YYYY-MM-DD')}
                                >
                                    <div className="timeline-item-content">
                                        <div className="timeline-procedure">
                                            <MedicineBoxOutlined /> {t(`tooth:procedures.${treatment.procedure}`, treatment.procedure)}
                                        </div>
                                        {treatment.notes && (
                                            <div className="timeline-notes">
                                                <FileTextOutlined /> {treatment.notes}
                                            </div>
                                        )}
                                        <div className="timeline-condition">
                                            {t('treatment:resultCondition')}:
                                            <Tag color={treatmentCondition.borderColor} style={{ marginLeft: 8 }}>
                                                {treatmentCondition.icon} {t(treatmentCondition.label)}
                                            </Tag>
                                        </div>
                                    </div>
                                </Timeline.Item>
                            );
                        })}
                    </Timeline>

                    {/* Detailed Table */}
                    <Divider />
                    <Table
                        dataSource={treatmentHistory}
                        columns={columns}
                        pagination={{ pageSize: 5 }}
                        size="small"
                        rowKey={(record, index) => index}
                    />
                </>
            ) : (
                <Empty
                    description={t('treatment:noHistory')}
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
            )}

            {/* Notes Section */}
            {notes && (
                <>
                    <Divider orientation="left">
                        <FileTextOutlined /> {t('common:notes')}
                    </Divider>
                    <div className="tooth-notes-section">
                        {notes}
                    </div>
                </>
            )}

            {/* Images Section */}
            {images && images.length > 0 && (
                <>
                    <Divider orientation="left">
                        {t('common:images')}
                    </Divider>
                    <div className="tooth-images-section">
                        <Row gutter={[8, 8]}>
                            {images.map((image, index) => (
                                <Col span={6} key={index}>
                                    <img
                                        src={image.url}
                                        alt={`Tooth ${toothNumber} - ${index + 1}`}
                                        className="tooth-detail-image"
                                    />
                                </Col>
                            ))}
                        </Row>
                    </div>
                </>
            )}
        </Modal>
    );
};

export default ToothDetailModal;
